// lib/feed/feed-filters-persistence.ts
//
// Pure domain for persisting the feed page's FILTERS (the full `FeedFilters` =
// `FeedFilterState & { sortBy }`) across navigation (sessionStorage + TTL +
// defensive coercion). Mirrors `lib/feed/feed-active-tab-persistence.ts` (hex:
// functional core — no DOM/window access; the adapter,
// `components/feed/feed-page-client-state.ts`, does the sessionStorage I/O in
// a `useState` lazy initializer + a persist effect and calls into these pure
// functions).
//
// WHY: without this, back-nav from a listing detail always re-mounts the feed
// page with DEFAULT filters (empty arrays, no gradeRange, no sort) even if the
// user had applied brand/theme/condition/grade filters or a sort. The feed
// snapshot cache (`lib/feed/feed-snapshot.ts`) keys its entries by
// `{mode, userId, limit, filtersKey}`, where `filtersKey` encodes
// filters+sortBy — so a remount with reset filters looks up a DIFFERENT cache
// key than the one the filtered browse session wrote to (cache miss), and
// items/page/cursor/scroll all appear "reset" too, even though the snapshot
// machinery itself is correct. Restoring filters here is what makes the
// existing snapshot lookup hit again — no changes needed in feed-snapshot.ts
// or hooks/use-paginated-feed.ts.
//
// @see components/feed/feed-page-client-state.ts (adapter)
// @see lib/feed/feed-active-tab-persistence.ts (twin persistence; the pattern
//   this file mirrors)
// @see lib/feed/feed-snapshot.ts (the cache this restoration re-aligns with)
// @see .tondocs/skills/hex.md §1 (functional core, imperative shell)

import { LISTING_CATEGORIES } from "@kraahl/contract";
import type { FeedFilters, FeedSortOption } from "@/components/feed/feed-page-client-state";
import { FEED_ACTIVE_TAB_TTL_MS } from "@/lib/feed/feed-active-tab-persistence";

/** sessionStorage key. Bump the version suffix if the persisted shape changes. */
export const FEED_FILTERS_STORAGE_KEY = "kraahl_feed_filters";

/** Bump when the persisted shape changes — old entries are then rejected on read. */
export const FEED_FILTERS_VERSION = 1;

/**
 * Same TTL as the active tab (`FEED_ACTIVE_TAB_TTL_MS`). Deliberately reused
 * rather than a separate literal: tab and filters are restored together on
 * the same remount, so if one had its own (shorter/longer) TTL, a restored
 * mount could show a stale-but-still-fresh tab paired with reset-to-default
 * filters (or vice versa) — internally "correct" per each value's own TTL,
 * but reads as a bug to the user, who experiences the feed page as one thing.
 */
export const FEED_FILTERS_TTL_MS = FEED_ACTIVE_TAB_TTL_MS;

/**
 * Same defaults `feed-page-client-state.ts` seeds its `filters` state with.
 * Exported so the adapter has one place to source the default from instead of
 * duplicating the literal.
 */
export const FEED_DEFAULT_FILTERS: FeedFilters = {
  brands: [],
  themes: [],
  categories: [],
  conditions: [],
  gradeRange: null,
  sortBy: null,
};

interface StoredFeedFilters {
  v: number;
  filters: FeedFilters;
  ts: number;
}

const KNOWN_SORT_OPTIONS: readonly Exclude<FeedSortOption, null>[] = [
  "price-asc",
  "price-desc",
  "date-asc",
  "date-desc",
];

// Canonical tuple — a persisted category from an older session that is no
// longer (or not yet) in LISTING_CATEGORIES is dropped by the coercion below.
const KNOWN_LISTING_CATEGORIES: readonly NonNullable<FeedFilters["listingCategory"]>[] =
  LISTING_CATEGORIES;

function isString(value: unknown): value is string {
  return typeof value === "string";
}

/** Required string-array field: non-string entries dropped; missing/malformed → `[]`. */
function coerceStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(isString) : [];
}

/**
 * Optional string-array field: only materializes when the raw value was
 * itself an array (even an empty one) — otherwise `undefined`, so a filter
 * panel section the user never touched stays absent rather than turning into
 * an explicit `[]` a consumer could mistake for "cleared".
 */
function coerceOptionalStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter(isString) : undefined;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** `[number, number]` tuple or `null`; any other shape degrades to `null`. */
function coerceGradeRange(value: unknown): [number, number] | null {
  if (!Array.isArray(value) || value.length !== 2) return null;
  const [lo, hi] = value;
  if (!isFiniteNumber(lo) || !isFiniteNumber(hi)) return null;
  return [lo, hi];
}

/** Optional variant of `coerceGradeRange` — `undefined` stays `undefined`. */
function coerceOptionalGradeRange(value: unknown): [number, number] | null | undefined {
  if (value === undefined) return undefined;
  return coerceGradeRange(value);
}

function coerceSortBy(value: unknown): FeedSortOption {
  return (KNOWN_SORT_OPTIONS as readonly unknown[]).includes(value) ? (value as FeedSortOption) : null;
}

function coerceListingCategory(value: unknown): FeedFilters["listingCategory"] {
  if (value === undefined) return undefined;
  return (KNOWN_LISTING_CATEGORIES as readonly unknown[]).includes(value)
    ? (value as NonNullable<FeedFilters["listingCategory"]>)
    : null;
}

/**
 * Defensively rebuild a `FeedFilters` object from an untrusted parsed value.
 * Every field is coerced independently — a corrupt/missing/future-shape field
 * falls back to its default rather than rejecting the whole restore, so a
 * partially garbled sessionStorage entry (hand-edited devtools, a field added
 * by a differently-versioned tab, a browser extension mangling storage)
 * still restores whatever it validly can. Exported so its per-field behavior
 * is directly unit-testable without going through the storage-string layer.
 */
export function coerceFeedFilters(raw: unknown): FeedFilters {
  const r = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  return {
    brands: coerceStringArray(r.brands),
    themes: coerceStringArray(r.themes),
    categories: coerceStringArray(r.categories),
    tcgs: coerceOptionalStringArray(r.tcgs),
    conditions: coerceStringArray(r.conditions),
    gradeRange: coerceGradeRange(r.gradeRange),
    listingFormats: coerceOptionalStringArray(r.listingFormats),
    tcgSets: coerceOptionalStringArray(r.tcgSets),
    tcgRarities: coerceOptionalStringArray(r.tcgRarities),
    cardConditions: coerceOptionalStringArray(r.cardConditions),
    cardLanguages: coerceOptionalStringArray(r.cardLanguages),
    cardFormats: coerceOptionalStringArray(r.cardFormats),
    gradingCompanies: coerceOptionalStringArray(r.gradingCompanies),
    cardGradeRange: coerceOptionalGradeRange(r.cardGradeRange),
    listingCategory: coerceListingCategory(r.listingCategory),
    mtgPrintVariants: coerceOptionalStringArray(r.mtgPrintVariants),
    sortBy: coerceSortBy(r.sortBy),
  };
}

/** Serialize the filters + write timestamp for storage. */
export function serializeFeedFilters(filters: FeedFilters, now: number): string {
  const payload: StoredFeedFilters = { v: FEED_FILTERS_VERSION, filters, ts: now };
  return JSON.stringify(payload);
}

/**
 * Decide which filters (if any) to restore from a raw sessionStorage value:
 * parse, validate shape/version, check freshness, then defensively coerce
 * every field. Returns `null` when nothing should be restored (missing/
 * malformed/expired/wrong-version) — the caller falls back to
 * `FEED_DEFAULT_FILTERS`. Pure — `now`/`ttlMs` are injected so tests are
 * deterministic and the domain never reads the clock.
 */
export function decideRestoredFeedFilters(raw: string | null, now: number, ttlMs: number): FeedFilters | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const p = parsed as Partial<StoredFeedFilters>;
  if (p.v !== FEED_FILTERS_VERSION) return null;
  const ts = typeof p.ts === "number" ? p.ts : 0;
  if (now - ts > ttlMs) return null;
  if (typeof p.filters !== "object" || p.filters === null) return null;
  return coerceFeedFilters(p.filters);
}
